/** Режим «Объединения» — порт renderProductionMergeList (build-скрипт, стр. 7924-8085):
 * группы-кандидаты на сведение варок (компоненты графа тегов), дорожки по неделям с
 * литражом и котлом, выбор целевой недели, переносы и подтверждения. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import * as planningApi from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import { int0 } from "./format";
import {
  mergeData,
  mergeGroups,
  mergeTargetRange,
  type MergeGroup,
  type MergeItem,
} from "./merge";
import {
  currentProductionWeekIndex,
  displayValue,
  productionBatchPlan,
  productionLiters,
  productionWeekIndex,
  productionYearWeeks,
  type ProductionWeek,
} from "./production";

interface ArticleInfo {
  article: string;
  name: string;
  ml: number;
  attrs: planningApi.ArticleAttrs | undefined;
}

export function ProductionMergeList({
  articles,
  weeks,
  production,
  year,
  canEdit,
}: {
  articles: ArticleInfo[];
  weeks: ProductionWeek[];
  production: {
    cells: Map<string, planningApi.ProductionCell[]>;
    produced: Set<string>;
    confirmations?: Map<string, planningApi.MergeConfirmation>;
  };
  year: number;
  canEdit: boolean;
}): React.ReactElement {
  const qc = useQueryClient();
  const currentWeek = currentProductionWeekIndex(year);
  const yearWeekCount = productionYearWeeks(year).length;

  const confirmedSet = useMemo(() => {
    const set = new Set<string>();
    for (const key of production.confirmations?.keys() ?? []) set.add(key);
    return set;
  }, [production.confirmations]);

  const { groups, stats } = useMemo(() => {
    const items: MergeItem[] = [];
    for (const info of articles) {
      const data = mergeData(info.attrs);
      if (!data.hasTags) continue;
      for (const w of weeks) {
        const key = `${info.article}|${w.key}`;
        const qty = displayValue(production.cells.get(key) ?? []);
        if (qty <= 0 || production.produced.has(key)) continue;
        items.push({
          article: info.article,
          name: info.name,
          weekKey: w.key,
          weekIndex: w.index,
          qty,
          liters: productionLiters(info.ml, qty),
          data,
          confirmed: confirmedSet.has(key),
        });
      }
    }
    return mergeGroups(items, currentWeek);
  }, [articles, weeks, production, currentWeek, confirmedSet]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["orders", "production"] });
  const movesMut = useMutation({
    mutationFn: (moves: { article: string; source_week: string; target_week: string }[]) =>
      planningApi.mergeMoves(year, moves),
    onSuccess: (r) => {
      invalidate();
      toast.success(`Перенесено позиций: ${r.moved}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Перенос не удался")),
  });
  const confirmMut = useMutation({
    mutationFn: async (v: { group: MergeGroup; confirm: boolean }) => {
      for (const item of v.group.items) {
        const matches = v.group.items
          .filter((x) => x !== item)
          .map((x) => ({ article: x.article, name: x.name, weekKey: x.weekKey, weekLabel: `Н${x.weekIndex}`, qty: x.qty }));
        if (v.confirm) {
          await planningApi.confirmMerge({ year, article: item.article, week_key: item.weekKey, matches });
        } else {
          await planningApi.unconfirmMerge({ year, article: item.article, week_key: item.weekKey });
        }
      }
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось изменить подтверждение")),
  });

  if (!groups.length) {
    return (
      <div className="rounded-md border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        Кандидатов на объединение нет: с тегами {stats.taggedTotal}, будущих {stats.futureTagged},
        прошедших {stats.pastTagged}. Теги объединения задаются в «Настройки → Справочник артикулов»
        (Продукт объединения / ПФ).
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span>Групп: <b className="text-foreground">{groups.length}</b></span>
        <span>Будущих позиций с тегами: <b className="text-foreground">{stats.futureTagged}</b></span>
        <span>Прошедших (не трогаем): <b className="text-foreground">{stats.pastTagged}</b></span>
        <span>Окно объединения: ±4 недели, от Н{stats.minWeek}</span>
      </div>
      {groups.map((group, gi) => (
        <MergeCard
          key={gi}
          group={group}
          currentWeek={currentWeek}
          yearWeekCount={yearWeekCount}
          canEdit={canEdit}
          onApply={(moves) => {
            if (window.confirm(`Перенести ${moves.length} позиций?`)) movesMut.mutate(moves);
          }}
          onConfirm={(confirm) => confirmMut.mutate({ group, confirm })}
        />
      ))}
    </div>
  );
}

function MergeCard({
  group,
  currentWeek,
  yearWeekCount,
  canEdit,
  onApply,
  onConfirm,
}: {
  group: MergeGroup;
  currentWeek: number;
  yearWeekCount: number;
  canEdit: boolean;
  onApply: (moves: { article: string; source_week: string; target_week: string }[]) => void;
  onConfirm: (confirm: boolean) => void;
}): React.ReactElement {
  const range = mergeTargetRange(group, currentWeek, yearWeekCount);
  const weekOptions = Array.from(
    { length: Math.max(0, range.max - range.min + 1) },
    (_, i) => range.min + i,
  );
  const year = Number(group.items[0]!.weekKey.slice(0, 4));
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(group.items.map((i) => `${i.article}|${i.weekKey}`)),
  );
  const [targets, setTargets] = useState<Map<string, number>>(new Map());
  const [laneTarget, setLaneTarget] = useState<number>(group.targetWeekIndex);

  const kettle = productionBatchPlan(group.totalLiters);
  const byWeek = useMemo(() => {
    const map = new Map<string, MergeItem[]>();
    for (const item of group.items) {
      const list = map.get(item.weekKey);
      if (list) list.push(item);
      else map.set(item.weekKey, [item]);
    }
    return [...map.entries()].sort(
      (a, b) => productionWeekIndex(a[0]) - productionWeekIndex(b[0]),
    );
  }, [group.items]);

  const buildMoves = (): { article: string; source_week: string; target_week: string }[] => {
    const moves: { article: string; source_week: string; target_week: string }[] = [];
    for (const item of group.items) {
      const key = `${item.article}|${item.weekKey}`;
      if (!selected.has(key)) continue;
      const target = targets.get(key) ?? laneTarget;
      const targetKey = `${year}-W${target}`;
      if (targetKey === item.weekKey) continue;
      moves.push({ article: item.article, source_week: item.weekKey, target_week: targetKey });
    }
    return moves;
  };

  return (
    <div
      className={cn(
        "rounded-md border bg-card p-3",
        group.confirmed && "border-emerald-300 dark:border-emerald-900",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-medium",
            group.confirmed
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
              : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
          )}
        >
          {group.confirmed ? "Проверено" : group.isMerged ? "Уже вместе" : "Кандидат"}
        </span>
        {group.tags.map((t) => (
          <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{t}</span>
        ))}
        <div className="ml-auto flex gap-4 text-xs text-muted-foreground">
          <span>Позиций: <b className="text-foreground">{group.items.length}</b></span>
          <span>Недель: <b className="text-foreground">{group.weekCount}</b></span>
          <span>Литраж: <b className="text-foreground">{group.totalLiters} л</b></span>
          <span>Котёл: <b className="text-foreground">{kettle.name}</b></span>
        </div>
      </div>

      <div className="mt-2 space-y-1.5">
        {byWeek.map(([weekKey, items]) => {
          const laneLiters = Math.round(items.reduce((s, i) => s + i.liters, 0) * 100) / 100;
          const laneKettle = productionBatchPlan(laneLiters);
          return (
            <details key={weekKey} className="rounded border bg-background px-2 py-1.5" open={items.length <= 4}>
              <summary className="cursor-pointer text-xs">
                <b>Н{productionWeekIndex(weekKey)}</b> · позиций {items.length} ·{" "}
                {int0(items.reduce((s, i) => s + i.qty, 0))} шт · {laneLiters} л · {laneKettle.name}
              </summary>
              <div className="mt-1.5 space-y-1">
                {items.map((item) => {
                  const key = `${item.article}|${item.weekKey}`;
                  return (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(key);
                            else next.delete(key);
                            return next;
                          })
                        }
                        title="В перенос"
                      />
                      <span className="w-32 shrink-0 truncate font-medium">{item.article}</span>
                      <span className="flex-1 truncate text-muted-foreground" title={item.name}>
                        {item.name}
                      </span>
                      <span className="tabular-nums">{int0(item.qty)} шт</span>
                      <select
                        className="h-6 rounded border bg-background px-1 text-[10px]"
                        disabled={!canEdit}
                        value={targets.get(key) ?? laneTarget}
                        onChange={(e) =>
                          setTargets((prev) => new Map(prev).set(key, Number(e.target.value)))
                        }
                      >
                        {weekOptions.map((w) => (
                          <option key={w} value={w}>→ Н{w}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>

      {canEdit && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Целевая неделя дорожки:</span>
          <select
            className="h-7 rounded border bg-background px-1 text-xs"
            value={laneTarget}
            onChange={(e) => {
              const v = Number(e.target.value);
              setLaneTarget(v);
              setTargets(new Map()); // селект дорожки массово переустанавливает детей
            }}
          >
            {weekOptions.map((w) => (
              <option key={w} value={w}>Н{w}</option>
            ))}
          </select>
          <Button size="sm" onClick={() => onApply(buildMoves())} disabled={!buildMoves().length}>
            Применить выбранные переносы
          </Button>
          {group.confirmed ? (
            <Button size="sm" variant="outline" onClick={() => onConfirm(false)}>
              Отменить подтверждение
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onConfirm(true)}>
              Подтвердить как проверенное
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
